import { t as tr } from './i18n';
import { intlLocale } from './i18n';
import { fmtNum, roDateTime } from './format';
import { daysText } from './package';
import type { OfferQuestion } from './questions';
import type { Rfq, Send } from './types';

/** Datele minime de care are nevoie șablonul de mail (merge și cu o cerere în lucru). */
export interface MailRfq {
  title: string;
  site: string;
  deadline: string;
  /** 'AAAA-LL-ZZ': când trebuie materialul pe șantier; lipsă = nu se spune */
  neededBy?: string | null;
  note: string;
  /** începutul mailului, scris de om; gol = `DEFAULT_RFQ_MESSAGE` */
  message?: string;
  /** numele firmei, la finalul subiectului; lipsă = subiectul fără el */
  company?: string;
  items: { desc: string; qty: number; unit: string; rentDays?: number | null }[];
}

/** Începutul obișnuit al cererii; omul îl poate rescrie înainte de trimitere. */
export const DEFAULT_RFQ_MESSAGE = 'Vă rugăm să ne transmiteți o ofertă de preț pentru produsele de mai jos.';

export interface MailContact {
  name?: string;
  contact?: string;
}

export interface Mail {
  subject: string;
  body: string;
}

/**
 * Conținutul unui mail către furnizor, pe bucăți. Din el se fac și textul pe
 * care îl copiază omul (`mailText`), și HTML-ul trimis din Procumi (pe
 * server), ca cele două să spună mereu același lucru.
 */
export interface MailDoc {
  subject: string;
  /** banda de sus: „Cerere de ofertă”, „Memento” */
  kind: string;
  /** titlul cererii, mare, în bandă */
  title: string;
  greeting: string;
  /** paragrafele de dinaintea datelor */
  intro: string[];
  /** caseta cu datele importante: termen, livrare */
  facts: { label: string; value: string }[];
  items: { desc: string; qty: string; detail?: string }[];
  /** mențiunile cererii; '' = fără */
  note: string;
  /** „Vă rugăm ca oferta să cuprindă:” și punctele; [] = fără listă */
  asksIntro: string;
  asks: string[];
  /** paragrafele de la final, înaintea semnăturii */
  outro: string[];
  signoff: string;
  signature: string;
  /** semnătura omului ca HTML (carte de vizită sau din Outlook); lipsă = textul de mai sus */
  signatureHtml?: string;
}

const GREETING = 'Bună ziua,';
const SIGNOFF = 'Cu stimă,';

/** Data întreagă, pentru un document: „16 octombrie 2026”. */
export const mailDate = (d: string): string =>
  new Date(`${d}T12:00:00`).toLocaleDateString(intlLocale(), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

const subjectOf = (title: string, company?: string) =>
  tr('Cerere de ofertă – {0}{1}', title, company?.trim() ? ` – ${company.trim()}` : '');

/** Ce trebuie să cuprindă orice ofertă, ca să poată fi comparată. */
const ASKS = [
  'prețurile unitare, fără TVA',
  'termenul de livrare',
  'condițiile de plată',
  'perioada de valabilitate a ofertei',
  'costul transportului, dacă nu este inclus în preț',
];

// Fără astea, o ofertă de chirie nu se poate compara cu alta (jurnalul, 28 septembrie).
const RENT_ASK =
  'pentru pozițiile la închiriere: chiria pe zi sau pe lună, chiria minimă, suprafața sau cantitatea acoperită de materialul ofertat, valoarea de înlocuire a materialului și costul transportului dus-întors';

/** Cererea de ofertă: un mail per furnizor, același pentru toți. */
export function rfqMailDoc(rfq: MailRfq, signature: string): MailDoc {
  const rent = rfq.items.some((it) => it.rentDays != null);
  return {
    subject: subjectOf(rfq.title, rfq.company),
    kind: tr('Cerere de ofertă'),
    title: rfq.title,
    greeting: tr(GREETING),
    intro: [rfq.message?.trim() || tr(DEFAULT_RFQ_MESSAGE)],
    facts: [
      { label: tr('Termen pentru ofertă'), value: mailDate(rfq.deadline) },
      ...(rfq.neededBy ? [{ label: tr('Necesar pe șantier'), value: mailDate(rfq.neededBy) }] : []),
      { label: tr('Adresa de livrare'), value: rfq.site },
    ],
    items: rfq.items.map((it) => ({
      desc: it.desc,
      qty: `${fmtNum(it.qty)} ${it.unit}`,
      ...(it.rentDays != null ? { detail: tr('închiriere, aproximativ {0}', daysText(it.rentDays)) } : {}),
    })),
    note: rfq.note.trim(),
    asksIntro: tr('Vă rugăm ca oferta să cuprindă:'),
    asks: (rent ? [...ASKS, RENT_ASK] : ASKS).map((a) => tr(a)),
    outro: [
      tr(
        'Vă rugăm să ne transmiteți oferta până la data de {0}. Pentru orice clarificare, vă stăm la dispoziție.',
        mailDate(rfq.deadline),
      ),
    ],
    signoff: tr(SIGNOFF),
    signature,
  };
}

/** Un mail fără tabel: follow-up, memento, întrebări. */
const letter = (
  o: Pick<MailDoc, 'subject' | 'kind' | 'title' | 'intro'> & Partial<MailDoc>,
  signature: string,
): MailDoc => ({
  greeting: tr(GREETING),
  facts: [],
  items: [],
  note: '',
  asksIntro: '',
  asks: [],
  outro: [],
  signoff: tr(SIGNOFF),
  signature,
  ...o,
});

/** Varianta text a mailului: ce copiază omul în programul lui și ce vede un program fără HTML. */
export function mailText(d: MailDoc): string {
  const blocks: string[] = [d.greeting, ...d.intro];
  if (d.facts.length) blocks.push(d.facts.map((f) => `${f.label}: ${f.value}`).join('\n'));
  if (d.items.length) {
    blocks.push(
      d.items
        .map((it, i) => `${i + 1}. ${it.desc} — ${it.qty}${it.detail ? ` (${it.detail})` : ''}`)
        .join('\n'),
    );
  }
  if (d.note) blocks.push(tr('Mențiuni: {0}', d.note));
  if (d.asks.length) blocks.push([d.asksIntro, ...d.asks.map((a) => `- ${a}`)].join('\n'));
  blocks.push(...d.outro, `${d.signoff}\n${d.signature}`);
  return blocks.join('\n\n');
}

/** Cererea de ofertă, ca text de copiat (și ca rezervă pentru mailul HTML). */
export function buildMail(rfq: MailRfq, _supplier: MailContact | null | undefined, signature: string): Mail {
  const d = rfqMailDoc(rfq, signature);
  return { subject: d.subject, body: mailText(d) };
}

export function followUpDoc(
  rfq: Pick<Rfq, 'title' | 'deadline'>,
  send: Pick<Send, 'sentAt'>,
  signature: string,
): MailDoc {
  return letter(
    {
      subject: tr('Re: Cerere de ofertă – {0}', rfq.title),
      kind: tr('Revenire la cererea de ofertă'),
      title: rfq.title,
      intro: [
        tr(
          'Revin cu privire la cererea noastră de ofertă „{0}”{1}. Termenul de transmitere a ofertei este {2}.',
          rfq.title,
          send.sentAt ? tr(', transmisă pe {0}', roDateTime(send.sentAt)) : '',
          mailDate(rfq.deadline),
        ),
        tr('Vă rugăm să ne confirmați dacă ne puteți transmite o ofertă.'),
      ],
    },
    signature,
  );
}

/** Textul de follow-up, de copiat. */
export function buildFollowUp(
  rfq: Pick<Rfq, 'title' | 'deadline'>,
  send: Pick<Send, 'sentAt'>,
  _recipient: MailContact | null | undefined,
  signature: string,
): string {
  return mailText(followUpDoc(rfq, send, signature));
}

/**
 * Întrebările despre o ofertă primită: ce lipsește ca să poată fi comparată
 * (vezi `offerQuestions`). Răspunsul se trece în ofertă, iar comparația se reface.
 */
export function buildQuestions(
  rfq: Pick<Rfq, 'title'> & { site: string },
  _recipient: MailContact | null | undefined,
  questions: OfferQuestion[],
  signature: string,
): Mail {
  const d = letter(
    {
      subject: tr('Re: Cerere de ofertă – {0}', rfq.title),
      kind: tr('Clarificări la ofertă'),
      title: rfq.title,
      intro: [
        tr(
          'Vă mulțumim pentru oferta transmisă pentru „{0}”. Pentru a o putea compara cu celelalte oferte primite, avem nevoie de câteva precizări.',
          rfq.title,
        ),
      ],
      facts: [{ label: tr('Adresa de livrare'), value: rfq.site }],
      asksIntro: tr('Vă rugăm să ne precizați:'),
      asks: questions.map((q) => q.ask),
      outro: [tr('Vă mulțumim.')],
    },
    signature,
  );
  return { subject: d.subject, body: mailText(d) };
}

/** Mementoul automat, cu o zi înainte de termen, la cine n-a răspuns. */
export function reminderDoc(rfq: Pick<Rfq, 'title' | 'deadline'>, signature: string): MailDoc {
  return letter(
    {
      subject: tr('Memento: cerere de ofertă – {0}', rfq.title),
      kind: tr('Memento · cerere de ofertă'),
      title: rfq.title,
      intro: [
        tr(
          'Vă reamintim că termenul de transmitere a ofertei pentru „{0}” este mâine, {1}.',
          rfq.title,
          mailDate(rfq.deadline),
        ),
        tr(
          'Dacă nu ne puteți transmite o ofertă de această dată, vă rugăm să ne anunțați, pentru a ne putea organiza.',
        ),
      ],
    },
    signature,
  );
}

export function buildReminder(
  rfq: Pick<Rfq, 'title' | 'deadline'>,
  _recipient: MailContact | null | undefined,
  signature: string,
): Mail {
  const d = reminderDoc(rfq, signature);
  return { subject: d.subject, body: mailText(d) };
}
